export function IncreasedSecurityControl() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #7
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Increased Security and Control
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Maintain a legal claim to the property with the right to foreclose and reclaim ownership if the buyer defaults
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              You Retain a Lien on the Property
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              One of the most powerful protections in seller financing is that you <strong>retain a legal lien on the property</strong> until the loan is fully paid. This means you maintain a secured interest in the real estate, giving you substantial legal rights and recourse if the buyer fails to make payments.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              If a buyer defaults on their payment obligations, you have the <strong>legal right to foreclose</strong> and take back ownership of the property. According to YouTube experts and real estate attorneys, this provides significantly more security than an unsecured loan or investment. You're not just hoping the buyer pays—you have a tangible asset backing your investment.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              In a foreclosure scenario, you keep all the payments received to date (which may represent years of principal and interest), regain full ownership of the property, and can resell it again—potentially with seller financing to a new buyer. This creates a win-win situation where you're protected regardless of the outcome.
            </p>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Your Legal Protections:</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>First Lien Position:</strong> Your lien is recorded and legally enforceable
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Foreclosure Rights:</strong> Legal ability to reclaim property upon default
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Keep All Payments:</strong> Retain principal and interest received before default
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Property Appreciation:</strong> Benefit from any property value increases
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Resale Opportunity:</strong> Sell the property again after foreclosure
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/130369197b514f4c555e6e4894385f53.jpg"
              alt="Legal security and control through property lien in seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
            />

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Foreclosure Example</h4>
              <p className="text-gray-700 mb-3">
                <strong>Scenario:</strong> You sell for $2M with $400K down and $1.6M financed at 7% over 15 years.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Year 5:</strong> Buyer defaults after making 60 payments totaling approximately $863,000 (including $263,000 in interest income).
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Your Position:</strong>
              </p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li>• You've received $400K down payment</li>
                <li>• You've received $863K in payments</li>
                <li>• You've earned $263K in interest</li>
                <li>• You foreclose and regain the property</li>
                <li>• Property may have appreciated in value</li>
                <li>• You can resell the property again</li>
              </ul>
              <p className="text-lg font-bold text-teal-600 mt-4">
                Total received: $1,263,000 + property ownership
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Seller Financing Is More Secure Than You Think
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-4-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Real Asset Backing</h4>
              <p className="text-gray-600 leading-relaxed">
                Unlike stocks, bonds, or unsecured loans, your investment is backed by a tangible real estate asset that typically appreciates over time
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Substantial Down Payment</h4>
              <p className="text-gray-600 leading-relaxed">
                Buyers typically provide 10-30% down payment, giving them significant equity and motivation to continue making payments
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-chart-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Property Appreciation</h4>
              <p className="text-gray-600 leading-relaxed">
                Even if foreclosure occurs, the property likely appreciated during the holding period, protecting your investment value
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=real%20estate%20foreclosure%20process%20with%20legal%20documents%20property%20ownership%20transfer%20back%20to%20seller%20professional%20legal%20consultation%20clean%20office%20setting%20bright%20professional%20background%20business%20transaction&width=800&height=600&seq=foreclosure-process-legal&orientation=landscape"
              alt="Foreclosure process and legal protections for sellers"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Additional Control Benefits
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Negotiate Terms:</strong>
                  <p className="text-gray-700">You control interest rates, loan terms, down payment requirements, and other conditions</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Choose Your Buyer:</strong>
                  <p className="text-gray-700">Screen buyers and select those with strong financials and experience</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Include Protective Covenants:</strong>
                  <p className="text-gray-700">Add clauses requiring property insurance, maintenance standards, and other protections</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Balloon Payment Options:</strong>
                  <p className="text-gray-700">Structure loans with balloon payments to reduce long-term risk</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Due-on-Sale Clauses:</strong>
                  <p className="text-gray-700">Prevent buyers from transferring property without your approval</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900 text-lg">Regular Payment Monitoring:</strong>
                  <p className="text-gray-700">Track payments and address issues early before they become major problems</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">The Bottom Line on Security</h3>
          <p className="text-xl text-gray-300 text-center max-w-4xl mx-auto leading-relaxed mb-8">
            Seller financing provides multiple layers of protection: substantial down payments, real estate collateral, legal foreclosure rights, and the ability to resell the property. You're not just lending money—you're making a secured investment backed by a valuable asset.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">10-30%</div>
              <div className="text-gray-300">Typical Down Payment</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-gray-300">Asset-Backed Security</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">Full</div>
              <div className="text-gray-300">Foreclosure Rights</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}